import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@material-ui/core';
import create from 'zustand';
import { IcdCptForm } from '../components/coding-tool/icd-cpt';
import { Api } from './api';
import { auth } from './auth';
import { changeReasonFormApi, ChangeReasons } from './change-form';
import { ReviseCodeForm } from './codesets-search';
import { Auto, CheckBox, Text } from './controls';
import { Audit, ChangeReason, CodeSet, Diagnosis, EditAction, Person, Procedure, Worksheet } from './domain';
import { FormApi, formApi } from './form';
import { splitCamelCase, Stringify, stringifyProcedure } from './format';
import { IcdCptApi } from './icd-cpt-api';
import { CodingConfig, VisitCode, WorksheetApis } from './visit-context';
import { currentWorkflow, isReviewWorkflowState } from './workflow-api';
import { elementCode, WorksheetElement } from './worksheets';
import { RebuttalAction } from './audit-rebuttal/audit-rebuttal-model';

type AuditData = {
  auditOpen: boolean;
  title: any;
  content: any;
  audit: Audit;
  form: FormApi<AuditFormData>;
  onSubmit: () => Promise<boolean>;
  onCancel: () => void;
};

type AuditActions = {
  openAuditWindow(open: boolean, props?: Partial<AuditData>): void;
};

export type AuditApi = Api<any, AuditData, AuditActions>;
function auditApi(): AuditApi {

  const data = create<AuditData>(() => ({
    auditOpen: false,
    title: null,
    content: null,
    audit: null,
    form: null,
    onSubmit: null,
    onCancel: null,
  }));

  const actions = {
    openAuditWindow: (auditOpen: boolean, props?: AuditData) => {
      data.setState({ ...props, ...{ auditOpen } });
    },
  };
  return { configuration: {}, data, actions };
}

export function AuditDialogue() {
  const { title, content, form, onCancel } = audit.data.getState();
  const open = audit.data((s) => s.auditOpen);

  const onClose = () => {
    audit.actions.openAuditWindow(false);
    if (onCancel) {
      onCancel();
    }
  };

  const onSubmit = async () => {
    const fn = audit.data.getState().onSubmit;
    if (fn) {
      const complete = await fn();
      if (complete) {
        audit.actions.openAuditWindow(false);
      }
    }
  };

  const ctl = form?.controls?.excludeFromAudit;
  return (
    <Dialog open={open} maxWidth={false}>
      <DialogTitle>
        <Grid container direction="row" alignContent="center" alignItems="center">
          <Grid item>{title}</Grid>
          <Grid item style={{ flexGrow: 1 }}></Grid>
          <Grid>{ctl && <CheckBox {...ctl} />}</Grid>
        </Grid>
      </DialogTitle>
      <DialogContent
        style={{
          paddingTop: 0,
          zIndex: 1501,
          marginLeft: 12,
        }}
      >
        {content}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="outlined">
          {' '}
          Cancel{' '}
        </Button>
        <Button onClick={onSubmit} color="primary" variant="contained">
          {' '}
          Submit{' '}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const audit = auditApi();

const width = { width: '100%' };
type IcdCptProps = {
  api: IcdCptApi;
  title?: string;
};
export function openCptAudit({ api, title }: IcdCptProps) {
  const commentForm = formApi({
    values: { comments: null, excludeFromAudit: false },
    fields: {
      comments: {
        label: 'Comments',
        style: { width: '100%' },
        multiline: true,
        rows: 2,
      },
      excludeFromAudit: {
        label: 'Education Only?',
        useBooleanValue: true,
      },
    },
  });

  audit.actions.openAuditWindow(true, {
    title,
    content: (
      <Grid container direction="column" style={{ ...width, ...{ padding: 0 } }}>
        <IcdCptForm icdCpt={api} />

        <Grid item style={{ ...width, ...{ padding: 0 } }}>
          <Text {...commentForm.controls.comments} />
        </Grid>

        <Grid item style={{ ...width, ...{ flexGrow: 1, paddingBottom: 8 } }}></Grid>
      </Grid>
    ),
  });
}

type AuditFormData = {
  reason?: VisitCode;
  notes?: string;
  excludeFromAudit?: boolean;
};

function auditReasonForm(list: VisitCode[], onSubmit: (form: AuditFormData) => void, existing?: Audit): FormApi<AuditFormData> {
  const i = existing?.changeReasons?.length;
  const changeReason: ChangeReason = i > 0 ? existing.changeReasons[i - 1] : null;
  const reason = changeReason ? list.filter((c) => c.code === changeReason.changeReasonCode)[0] : null;

  return formApi<AuditFormData>({
    values: {
      reason,
      notes: existing?.notes ? existing.notes : (null as any),
      excludeFromAudit: existing?.excludeFromAudit ? existing.excludeFromAudit : false,
    },
    fields: {
      reason: {
        required: true,
        label: 'Change Reason',
        list,
        listLabel: (v: VisitCode) => v.description,
      },
      notes: {
        label: 'Audit Comments',
        style: { width: '100%' },
        multiline: true,
        rows: 2,
      },
      excludeFromAudit: {
        label: 'Education Only?',
        useBooleanValue: true,
      },
    },
    valid: (v) => !!v.reason,
    onSubmit: (v) => {
      onSubmit(v);
    },
  });
}

type AuditActionProps = {
  title: string;
  list: VisitCode[];
  onComplete: (a: Audit) => void;
  target?: CodeSet;
  existing?: Audit;
  revise?: boolean;
  onCancel?: () => void;
};

export function openAuditAction(props: AuditActionProps) {
  const { title, list, onComplete, revise, target } = props;
  let existing = props.existing;

  const form: FormApi = auditReasonForm(
    list,
    (values) => {
      const update: Audit = { ...existing };
      const { code, description } = values.reason;

      update.changeReasonCode = code as string;
      update.changeReasonDescription = description;
      update.notes = values.notes;
      update.excludeFromAudit = values.excludeFromAudit;
      update.changeReasons = update.changeReasons || [];

      const reason: ChangeReason = {
        fieldName: 'object',
        code: elementCode(target),
        oldValue: '',
        newValue: '',
        changeReasonCode: update.changeReasonCode,
        changeReasonDescription: update.changeReasonDescription,
      };

      if (!revise) {
        update.changeReasons.push(reason);
      }

      // if (!update.createdBy || !update.createdBy?.firstName) {
      //   update.createdBy = auth.signedIn;
      // }

      form.valid.setState({ value: true });
      onComplete(update);
    },
    existing
  );
  const { notes, reason, excludeFromAudit } = form.controls;

  //TODO other worksheet elements...
  const onReviseComplete = (code: Diagnosis | Procedure | any) => {
    code = code || target;
    if (code) {
      code.audit = {
        ...{ createdBy: auditPerson(auth.signedIn) },
        ...existing,
        ...code?.audit,
        ...{
          action: 'UPDATE',
          code: code.diagnosisCode || code.cptCode,
        },
      };

      code.code = code.diagnosisCode || code.cptCode;

      if (code.diagnosisCode) {
        code.diagnosisCode = code.audit.code;
      } else {
        code.cptCode = code.audit.code;
      }
      form.submit();

      existing = { ...existing, ...code.audit };

      onComplete(code.audit);
    }
  };

  audit.actions.openAuditWindow(true, {
    title,
    form,
    onSubmit: async () => {
      await form.submit();
      return form.valid.getState().value;
    },
    content: (
      <Grid
        container
        direction="column"
        spacing={2}
        style={{
          ...width,
          ...{ paddingTop: 0, width: 346 },
        }}
      >
        {list?.length > 0 && (
          <Grid item style={{ ...width }}>
            <Auto {...reason} />
          </Grid>
        )}
        {revise && (
          <Grid item style={width}>
            <ReviseCodeForm target={target} onCodeFound={onReviseComplete} />
          </Grid>
        )}

        <Grid item style={width}>
          <Text {...notes} />
        </Grid>

        <Grid item style={{ ...width, ...{ flexGrow: 1, paddingBottom: 8 } }}></Grid>
      </Grid>
    ),
  });
}

type AuditChangeActionProps = {
  title: string;
  original: any;
  update: any;
  changeReasons: ChangeReasons;
  fieldNames: string[];
  onComplete: (a: Audit) => void;
  onNoChanges?: () => void;
  existing?: Audit;
  onCancel?: () => void;
};

export function openAuditChangeAction(props: AuditChangeActionProps) {
  const { title, original, update, changeReasons, onComplete, onNoChanges, fieldNames, onCancel } = props;
  let existing = props.existing || original.audit;

  const api = changeReasonFormApi({
    original,
    update,
    changeReasons,
    fieldNames,
  });

  if (!api?.reasons.length) {
    if (onNoChanges) {
      onNoChanges();
    }
    return;
  }

  const { form, component } = api;

  audit.actions.openAuditWindow(true, {
    title,
    form,
    onCancel,
    onSubmit: () => {
      return new Promise(async (resolve) => {
        await form.submit();

        const changeReasons = existing?.changeReasons || [];

        const a: Audit = { ...existing };
        a.changeReasons = changeReasons.concat(api.userChangeReasons());

        const values = form.values.getState().value;
        a.notes = values.notes || a.notes;
        a.excludeFromAudit = values.excludeFromAudit;

        const valid = form.valid.getState().value;
        if (valid && onComplete) {
          setTimeout(() => onComplete(a), 0);
          return resolve(valid);
        }
        return resolve(valid);
      });
    },
    content: (
      <Grid
        container
        direction="column"
        spacing={2}
        style={{
          ...width,
          ...{ paddingTop: 0, width: 900 },
        }}
      >
        <Grid item style={width}>
          {component}
        </Grid>

        <Grid item style={{ ...width, ...{ flexGrow: 1, paddingBottom: 8 } }}></Grid>
      </Grid>
    ),
  });
}

export function reviewActionOr(props: AuditActionProps, fn: () => void, reviewGuard?: () => boolean): void {
  const guard = reviewGuard || isReviewWorkflowState;
  if (guard()) {
    openAuditAction(props);
  } else {
    fn();
  }
}

export function reviewChangesOr(props: AuditChangeActionProps, fn: () => void): void {
  if (isReviewWorkflowState()) {
    props.onNoChanges = fn;
    openAuditChangeAction(props);
  } else {
    fn();
  }
}

export function editAction(props: AuditActionProps): void {
  openAuditAction(props);
}

export const onEditAction =
  (title: string, onComplete: (item?: any) => void, revise = false, list = []) =>
  (e: any) => {
    const audit: Audit = e.value.audit;

    editAction({
      title,
      revise,
      list: list || [],
      existing: audit,
      target: e.value,
      onComplete: (a: Audit) => {
        if (revise) {

          a.action = 'UPDATE';

          audit.changeReasonCode = a.changeReasonCode;
          audit.changeReasonDescription = a.changeReasonDescription;
          audit.notes = a.notes;

          const i = audit.changeReasons.length;
          if (i) {
            const b = audit.changeReasons[i - 1];
            if (b) {
              b.changeReasonCode = a.changeReasonCode;
              b.changeReasonDescription = a.changeReasonDescription;
            }
          }
        }
        e.value.audit = { ...e.value.audit, ...a };

        if (onComplete) {
          onComplete(e.value);
        }
      },
    });
  };

export type AuditGroup = {
  workflow: string;
  added: WorksheetElement[];
  deleted: WorksheetElement[];
  moved: WorksheetElement[];
  revised: WorksheetElement[];
  show: boolean;
};
export type AuditSummary = {
  worksheet: Worksheet;
  icd: AuditGroup;
  cpt: AuditGroup;
  downCodes: AuditGroup;
  lostRevenue: AuditGroup;
  tracking: AuditGroup;
  sendBacks: AuditGroup;
  reasonForVisit: AuditGroup;
  ncnbReasons: AuditGroup;
  workflow: string;
  api: WorksheetApis;
  currentWorkflow: string;
  show?: boolean;
  rebuttal?: RebuttalAction;
  config?: CodingConfig

};

export function getElementAudit(e: WorksheetElement, workflow: string): Partial<Audit> {
  if (!e) {
    return {};
  }
  const map = e.auditMap || {};
  const withWorkflow = Object.entries(map).filter(([k, _v]) => k === workflow);

  if (!withWorkflow.length) {
    return {};
  }

  if (withWorkflow.length > 1) {
    console.info('Multiple change reasons for single element', withWorkflow);
  }

  const obj = withWorkflow[0][1];
  return obj;
}

const previousKey = 'previous';
export function setPreviousMap(e: WorksheetElement, a: Partial<Audit>) {
  if(e.auditMap) {
      e.auditMap[previousKey] = a;
  }
}
export function getPreviousMap(e: WorksheetElement): Partial<Audit> {
  if (!e) {
    return {};
  }
  const map = e.auditMap || {};
  return map[previousKey];
}

export function getAllElementAudits(e: WorksheetElement): Partial<Audit>[] {
  if (!e) {
    return [];
  }
  const map = e.auditMap || {};
  const list: Audit[] = [];

  Object.entries(map).forEach(([workflow, v]) => {
    if (v.changeReasons?.length) {
      list.push({ ...v, workflow });
    }
  });
  return list;
}

function auditSummary(api: WorksheetApis, workflow: string): AuditSummary {
  const { worksheet, rebuttal, config } = api.context;
  console.info('auditSummary for workflow', workflow);

  const filter = (t: EditAction) => (v: WorksheetElement) => {
    const disagreementReasons = v.audit?.disagreementReasons;
    const sourceObject = v.audit?.sourceObject;
    const rebuttalReasons = v.audit?.rebuttalReasons;
    const lineId = v.audit?.lineId;
    const audit = getElementAudit(v, workflow);

    if (audit) {
      try {
        const map = v.auditMap || {};
        const existing = map.hasOwnProperty[workflow] ? map[workflow] : {};
        map[workflow] = JSON.parse(JSON.stringify({ ...existing, ...audit }));
        v.auditMap = map;
        map[workflow].disagreementReasons = undefined;

      } catch (e) {
        console.info('audit parse error', e);
      }
    }

    v.audit = audit;


    if (v.audit) {
      v.audit.disagreementReasons = disagreementReasons;
      v.audit.sourceObject = sourceObject;
      v.audit.rebuttalReasons = rebuttalReasons;
      v.audit.workflow = workflow;
      v.audit.lineId = lineId;
    }
    return audit?.action === t;
  };

  const add = filter('CREATE');
  const remove = filter('DELETE');
  const revise = filter('UPDATE');

  function group(list: WorksheetElement[] = [], moved = []): AuditGroup {
    const g: AuditGroup = {
      workflow,
      moved,
      added: list.filter(add),
      deleted: list.filter(remove),
      revised: list.filter(revise),
      show: false,
    };
    g.show = !!(g.added?.length || g.deleted?.length || g.moved?.length || g.revised?.length);
    return g;
  }

  const { diagnoses, procedures, reasonForVisit: rfv } = api.icdCpt.appendInactive(api.icdCpt.current());

  const icd: AuditGroup = group(diagnoses, (worksheet.diagnosisMoves || []).filter((m) => m.workflow === workflow));
  const cpt: AuditGroup = group(procedures, (worksheet.procedureMoves || []).filter((m) => m.workflow === workflow));

  const downCodes: AuditGroup = group(api.downCode.list.data.getState().all);
  const lostRevenue = group(api.lostRevenue.list.data.getState().all);
  const tracking = group(api.trackingCode.list.data.getState().all);
  const ncnbReasons = group(api.ncnbReasons.list.data.getState().all);
  
  const sendBacks = group(api.returns.list.data.getState().all);
  const reasonForVisit = group(rfv);


  return {
    worksheet,
    workflow,
    api,
    currentWorkflow: api.context.workflow,
    icd,
    cpt,
    downCodes,
    lostRevenue,
    tracking,
    sendBacks,
    reasonForVisit,
    ncnbReasons,
    rebuttal,
    config,
    show: !!(icd.show || cpt.show || downCodes.show || lostRevenue.show || tracking.show || reasonForVisit.show || sendBacks.show || ncnbReasons.show),
  };
}

export function auditSummaries(apis: WorksheetApis[], workflows: string[]): AuditSummary[] {
  return (apis || []).map((v) => workflows.map((workflow) => auditSummary(v, workflow))).flat();
}

// Extract only the identity fields from a signed-in user profile. auth.signedIn
// is the full UserProfile carrying the entire activities/organization tree
// (~2MB); persisting all of it into audit.createdBy bloats the send-back payload.
export function auditPerson(p: any): Person {
  if (!p) {
    return p;
  }
  const { principalId, userId, email, firstName, lastName, login } = p;
  return { principalId, userId, email, firstName, lastName, login };
}

export function updateElementAudit(el: WorksheetElement, update: Audit, add = false): void {
  const a: Audit = { ...{ changeReasons: [] }, ...el?.audit, ...update };

  if (a.changeReasons.length && !add) {
    const r = a.changeReasons[a.changeReasons.length - 1];
    a.changeReasonCode = a.changeReasonCode || r.changeReasonCode;
    a.changeReasonDescription = a.changeReasonDescription || r.changeReasonDescription;
  } else {
    a.changeReasons = a.changeReasons || [];
    if (update.changeReasons.length) {
      a.changeReasons.push(update.changeReasons[0]);
    }
  }

  a.action = a.action || 'UPDATE';
  // Attribute this change to the user performing it. Without this, editing an
  // existing code inherits the original code's `createdBy` and the audit summary
  // shows the previous user, while newly added codes (no createdBy) fall back to
  // the signed-in user. Setting it here keeps add/edit/delete consistent.
  //
  // Only copy identity fields — auth.signedIn is the full UserProfile (with the
  // entire activities/organization/location tree, ~2MB). It lands in the payload
  // up to 3x (audit.createdBy, auditMap[workflow], auditMap.previous), which blew
  // the send-back invoke past Lambda's 6MB cap (413 LambdaException).
  if (auth.signedIn) {
    a.createdBy = auditPerson(auth.signedIn);
  }

  if (el) {
    a.code = elementCode(el) || a.code;
    el.audit = a;

    el.auditMap = el.auditMap || {};
    const current = currentWorkflow();
    el.auditMap[current] = a;
    el.audit = el.auditMap[current];

    const p = getPreviousMap(el);
    if(!p){
      setPreviousMap (el, a);
    }
  }

  // console.info('updateElementAudit', update, el);
}

const stringify: Stringify<Procedure> = stringifyProcedure({});

export function auditTooltip(a: Audit): string {
  if (!a) {
    return '';
  }

  let out = 'AUDIT CHANGES:\n\n';

  function add(v: string) {
    if (!v?.length) {
      return;
    }
    out += v;
    out += '\n';
  }

  const defaultLabel = (v: any) => JSON.stringify(v);

  if (a.changeReasons?.length) {
    a.changeReasons.forEach((v: ChangeReason) => {
      if (v.fieldName === 'object') {
        add(a.action + ': ' + v.changeReasonDescription);
      } else {
        add(splitCamelCase(v.fieldName));
        add(v.changeReasonDescription);

        if (v.newValue) {
          const valueLabel = stringify[v.fieldName] || defaultLabel;
          add(`Old value: ${valueLabel(v.oldValue)} New Value: ${valueLabel(v.newValue)}`);
        }
      }
      out += '\n';
    });
  }

  out += '\n';
  add(a.notes);
  return out;
}
